const env = import.meta.env

export async function fetchSearch(keyword: string, filters: any[], pageN: any, related?: string, geoFilter?: { minLon: number; minLat: number; maxLon: number; maxLat: number } | null, sort_by?: string | null, sort_order?: 'asc' | 'desc', size?: number | string) {
    const url = !related ? `${env.VITE_API_URL}/${env.VITE_SEARCH_ENDPOINT}` : `${env.VITE_API_URL}/${env.VITE_SEARCH_ENDPOINT}/within-relations`;
    const pageSize = size ?? env.VITE_PAGE_SIZE;
    const body = !related ? {
        query: keyword,
        filters: filters,
        page: pageN,
        size: pageSize,
        ...(geoFilter ? { geoFilter } : {}),
        ...(sort_by ? { sort_by, sort_order: sort_order ?? 'asc' } : {}),
    } : {
        query: keyword ? keyword : "*",
        filters: [
            {
                field: "identifier",
                values: [related]
            }
        ],
        page: pageN,
        size: pageSize,
    }
    const options: RequestInit = {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
    };

    try {
        const response = await fetch(url, options);

        if (!response.ok) {
            console.error('HTTP Error - Request details:', {
                url,
                method: options.method,
                headers: options.headers,
                body: body,
                timestamp: new Date().toISOString()
            });
            console.log(response.body)
            throw new Error(`HTTP error! status: ${response.status}`);
        }


        const data = await response.json();
        return data;
    } catch (error: any) {
        console.error('Error fetching search results:', error.message);
        throw error;
    }
}

export async function fetchDetails(identifier: string) {
    const url = `${env.VITE_API_URL}/${env.VITE_DETAILS_ENDPOINT}/${identifier}`
    const options: RequestInit = {
        method: 'GET',
        headers: {
            'Accept': 'application/json'
        },
    };

    try {
        const response = await fetch(url, options);

        if (!response.ok) {
            console.error('Request Error - Request details:', {
                url,
                method: options.method,
                headers: options.headers,
                identifier,
                status: response.status,
                statusText: response.statusText,
                timestamp: new Date().toISOString()
            });

            // Provide specific error message for 404
            if (response.status === 404) {
                throw new Error(`HTTP error! status: 404`);
            }

            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error: any) {
        console.error('Error fetching details:', error.message);
        throw error;
    }
}

export async function fetchDigitalObjects(identifier: string, sortColumn: string | null = null, sortDirection: string | null = null, pageNumber: number = 0, pageSize: number = 25, filterText: string = "", sourceName: string = "") {
    if (!identifier) {
        throw new Error('Identifier is required to fetch digital objects');
        return;
    }
    const url = `${env.VITE_API_URL}/${env.VITE_DIGITAL_OBJECTS_ENDPOINT}`;
    const options: RequestInit = {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            "code": identifier,
            "pageNumber": pageNumber,
            "pageSize": pageSize,
            "sortColumn": sortColumn,
            "sortDirection": sortDirection,
            "sourceCode": null,
            "sourceAttributeName": sourceName || null,
            "sourceName": sourceName || null,
            "hierarchyLevel": null,
            "digitalObjectName": filterText || null
        })
    };

    try {
        const response = await fetch(url, options);
        if (!response.ok) {
            if (response.status === 500) {
                console.error('500 Error - Request details:', {
                    url,
                    method: options.method,
                    headers: options.headers,
                    body: JSON.parse(options.body as string),
                    identifier,
                    sortColumn,
                    sortDirection,
                    pageNumber,
                    pageSize,
                    filterText,
                    timestamp: new Date().toISOString()
                });
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return data;
    } catch (error: any) {
        console.error('Error fetching digital objects:', error.message);
        throw error;
    }
}

export async function fetchRelationsByGeo(
    geoFilter: { minLon: number; minLat: number; maxLon: number; maxLat: number },
    query: string,
    filters: any[],
    page: number,
    size: number,
    sort_by?: string | null,
    sort_order?: 'asc' | 'desc'
) {
    const url = `${env.VITE_API_URL}/${env.VITE_SEARCH_ENDPOINT}/relations-by-geo`;
    const body: any = {
        geoFilter,
        query: query?.trim() ? `*${query.trim()}*` : '**',
        filters: filters ?? [],
        page,
        size,
    };
    if (sort_by) {
        body.sort_by = sort_by;
        body.sort_order = sort_order ?? 'asc';
    }
    const options: RequestInit = {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
    };
    try {
        const response = await fetch(url, options);
        if (!response.ok) {
            console.error('HTTP Error - relations-by-geo request details:', {
                url,
                method: options.method,
                headers: options.headers,
                body,
                status: response.status,
                timestamp: new Date().toISOString(),
            });
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return data;
    } catch (error: any) {
        console.error('Error fetching relations by geo:', error.message);
        throw error;
    }
}

export async function fetchDigitalObjectSourcePaths(identifier: string) {
    const env = import.meta.env;
    const response = await fetch(`${env.VITE_API_URL}/digital_objects/${identifier}/source-paths`);

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
}